import { useEffect, useState } from 'react';
import { Selecteur } from '@journal/ui';
import { useSession } from '../lib/session.tsx';
import { useDonnees } from '../lib/donnees.ts';
import type { Classe } from './GestionClasses.tsx';

/**
 * Sur quelle classe on travaille.
 *
 * ── Il n'apparaît que s'il y a un choix à faire ─────────────────────────────
 * Un enseignant n'a qu'une classe : lui montrer une liste déroulante à un seul
 * élément lui poserait une question dont la réponse est évidente, sur chaque
 * écran, toute l'année. Le sélecteur ne se montre qu'à partir de deux — un
 * directeur, ou un remplaçant sur plusieurs classes.
 *
 * ── Une exception : la classe qu'on n'enseigne PAS ──────────────────────────
 * Un directeur qui ne fait pas classe n'a aucune classe par défaut. Compter les
 * classes ne suffit donc pas : une école d'une seule classe, dont il n'est pas
 * titulaire, lui masquait le sélecteur — et avec lui le seul chemin vers les
 * modules de son école.
 *
 * ── Le choix survit au rechargement ─────────────────────────────────────────
 * Un directeur qui prépare la classe de CM1 puis actualise la page ne doit pas
 * se retrouver en CE2 sans comprendre pourquoi. Le choix tient dans le stockage
 * local ; le serveur refuse de toute façon une classe hors de portée, donc une
 * valeur périmée ne donne accès à rien.
 */

const CLE = 'journal.classe';

/**
 * La classe choisie à la main, s'il y en a une.
 *
 * Exportée parce que deux écrans en dépendent : la barre latérale, qui décide
 * d'afficher les modules pédagogiques, et l'accueil, qui bascule entre le
 * récapitulatif du jour et l'écran d'administration. Ils lisaient jusqu'ici
 * « suis-je titulaire d'une classe ? » — vrai pour un enseignant, faux pour un
 * directeur, y compris après qu'il en a ouvert une.
 */
export function classeChoisie(): string | null {
  try {
    return globalThis.localStorage?.getItem(CLE) || null;
  } catch {
    // Stockage refusé — navigation privée, réglage du navigateur. On retombe
    // sur « aucune classe choisie », ce qui est vrai et sans conséquence.
    return null;
  }
}

export function SelecteurClasse() {
  const { api } = useSession();
  const classes = useDonnees<Classe[]>('/classrooms');
  // Déjà chargé par la coquille : la comparaison ne coûte pas de requête de
  // plus, et c'est elle qui distingue « ma classe » de « celle d'un collègue ».
  const moi = useDonnees<{ id: string }>('/auth/me');
  const [choisie, setChoisie] = useState<string>(
    () => globalThis.localStorage?.getItem(CLE) ?? '',
  );

  const liste = classes.donnees ?? [];

  /*
   * Le choix est transmis au client API avant tout autre chargement.
   *
   * Sans cette synchronisation, les écrans monteraient leurs requêtes avec la
   * classe précédente et n'afficheraient la bonne qu'au rechargement suivant —
   * un décalage d'un cran, très difficile à interpréter pour qui l'observe.
   */
  useEffect(() => {
    if (liste.length === 0) return;
    const valide = liste.some((c) => c.id === choisie) ? choisie : '';
    api.definirClasse(valide || null);
    if (valide !== choisie) setChoisie(valide);
  }, [api, choisie, liste]);

  const siennes = liste.filter((c) => c.teacherId === moi.donnees?.id);
  // Une seule classe, et c'est la sienne : rien à demander.
  if (liste.length < 2 && liste.length === siennes.length) return null;

  return (
    <Selecteur
      libelle="Classe"
      value={choisie}
      onChange={(e) => {
        const valeur = e.target.value;
        setChoisie(valeur);
        api.definirClasse(valeur || null);
        if (valeur) globalThis.localStorage?.setItem(CLE, valeur);
        else globalThis.localStorage?.removeItem(CLE);
        /*
         * Rechargement complet plutôt qu'une invalidation ciblée.
         *
         * Changer de classe change TOUT : élèves, journal, progressions,
         * programmation, emploi du temps. Recharger chaque écran à la main
         * laisserait forcément une donnée de l'ancienne classe à l'écran, et
         * c'est précisément la confusion qu'on veut éviter ici.
         */
        globalThis.location?.reload();
      }}
      options={[
        /*
         * L'entrée vide veut dire « ma classe », et n'a de sens que si l'on en
         * a une. Un directeur qui n'enseigne pas la verrait comme une option
         * qui ne mène à rien — c'est-à-dire à l'écran d'administration.
         */
        {
          valeur: '',
          texte: siennes.length > 0 ? 'Ma classe' : 'Choisir une classe…',
        },
        ...liste.map((c) => ({
          valeur: c.id,
          texte: c.levels ? `${c.name} · ${c.levels}` : c.name,
        })),
      ]}
    />
  );
}
